<template>
    <div class="form">
        <section class="form-section">
            <div class="form-section-heading">
                {{ trans('wbt_configs.headline.options') }}
            </div>

            <div class="form-row">
                <div class="form-row__col form-row__col--one-half">
                    <label class="label">
                        {{ trans('wbt_configs.label.languages') }}
                    </label>
                    <v-select
                        v-model="fields.languages"
                        :options="languageOptions"
                        class="form-vue-select"
                        multiple
                    >
                        <div slot="no-options">
                            {{ trans('vue_select.no_options') }}
                        </div>
                    </v-select>
                    <input
                        :name="'selected_languages'"
                        :value="languagesSelectedIds"
                        hidden
                    />
                </div>
            </div>

            <div class="form-row">
                <div class="form-row__col form-row__col--one-half">
                    <checkbox name="skippable" v-model="fields.skippable">
                        {{ trans('wbt_configs.label.skippable') }}
                    </checkbox>
                </div>
                <div class="form-row__col form-row__col--one-half">
                    <checkbox
                        name="video_scrubbing"
                        v-model="fields.videoScrubbing"
                    >
                        {{ trans('wbt_configs.label.video_scrubbing') }}
                    </checkbox>
                </div>
            </div>

            <div class="form-row">
                <div class="form-row__col form-row__col--one-half">
                    <form-input
                        name="test_pass_rate"
                        :required="true"
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        v-model="fields.testPassRate"
                    >
                        {{ trans('wbt_configs.label.test_pass_rate') }}
                    </form-input>
                </div>
            </div>
        </section>

        <section class="form-section">
            <div class="form-section-heading">
                {{ trans('wbt_configs.headline.designs') }}
            </div>

            <div class="form-row">
                <div class="form-row__col">
                    <label class="label" for="custom_logo">{{
                        trans('wbt_configs.label.custom_logo')
                    }}</label>
                    <dropzone
                        :uploaded-file="fields.customLogo"
                        name="custom_logo"
                        @change="onChangeLogo"
                        @remove="onRemoveLogo"
                    >
                        {{ trans('courses.note.image_upload') }}
                    </dropzone>
                    <input hidden name="deleted_logo" v-model="deletedLogo" />
                </div>
            </div>

            <div class="form-row">
                <div class="form-row__col">
                    <label class="label" for="custom_css">{{
                        trans('wbt_configs.label.custom_css')
                    }}</label>
                    <dropzone
                        :uploaded-file="fields.customCss"
                        name="custom_css"
                        @change="onChangeCss"
                        @remove="onRemoveCss"
                    >
                        {{ trans('courses.note.image_upload') }}
                    </dropzone>
                    <input hidden name="deleted_css" v-model="deletedCss" />
                </div>
            </div>

            <div class="form-row">
                <div class="form-row__col">
                    <label class="label" for="custom_welcome_image">{{
                        trans('wbt_configs.label.custom_welcome_image')
                    }}</label>
                    <dropzone
                        :uploaded-file="fields.customWelcomeImage"
                        name="custom_welcome_image"
                        @change="onChangeIntroImage"
                        @remove="onRemoveIntroImage"
                    >
                        {{ trans('courses.note.image_upload') }}
                    </dropzone>
                    <input
                        hidden
                        name="deleted_intro_image"
                        v-model="deletedIntroImage"
                    />
                </div>
            </div>
        </section>

        <section class="form-section">
            <div class="form-section-heading">
                {{ trans('wbt_configs.headline.custom_texts') }}
            </div>

            <div class="form-row">
                <div class="form-row__col">
                    <label class="label">{{
                        trans('wbt_configs.label.text_verification')
                    }}</label>
                    <v-select
                        v-model="selectedText.verification"
                        :options="languageOptions"
                        class="form-vue-select"
                    >
                    </v-select>
                </div>
            </div>

            <div class="form-row">
                <div class="form-row__col">
                    <div v-for="language in languages" :key="language">
                        <form-text-area
                            :name="'text_verification_' + language"
                            :required="false"
                            v-model="fields.textVerification[language]"
                            v-show="selectedText.verification.value == language"
                        >
                        </form-text-area>
                    </div>
                </div>
            </div>
        </section>

        <section class="form-section">
            <div class="form-section-heading">
                {{ trans('wbt_configs.headline.courses') }}
            </div>

            <div class="form-row">
                <div class="form-row__col">
                    <label class="label">{{
                        trans('wbt_configs.label.courses')
                    }}</label>
                    <v-select
                        v-model="coursesSelected"
                        :options="courseOptions"
                        :clearable="false"
                        class="form-vue-select"
                        multiple
                        @input="courseSelectionChanged"
                    >
                        <div slot="no-options">
                            {{ trans('vue_select.no_options') }}
                        </div>
                    </v-select>
                    <input
                        :name="'selected_courses'"
                        :value="coursesSelectedIds"
                        hidden
                    />
                </div>
            </div>
        </section>

        <div class="page-control">
            <div class="page-control__element">
                <button type="submit" class="btn btn--big btn--primary">
                    {{ trans('buttons.submit') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        wbtConfig: {
            type: Object,
            default: () => {
                return {};
            },
        },
        courseOptions: {
            type: Array,
            default: () => {
                return [
                    { label: this.trans('wbt_configs.all_courses'), value: 0 },
                ];
            },
        },
        preselectedCourses: {
            type: Array,
        },
    },

    data() {
        const languages = ['de', 'en', 'fr', 'es', 'ro', 'hu', 'cz', 'pl'];
        let verificationObject = {};
        let languageOptionsArray = [];
        let selectedLanguages = [];
        languages.forEach(language => {
            verificationObject[language] =
                this.old(
                    'text_verification_' + language,
                    this.wbtConfig.custom_text.verification[language]
                ) || '';
            if (
                this.wbtConfig.languages &&
                this.wbtConfig.languages.includes(language)
            ) {
                selectedLanguages.push({
                    value: language,
                    label: this.trans(
                        'wbt_configs.language_options.' + language
                    ),
                });
            }
            languageOptionsArray.push({
                value: language,
                label: this.trans('wbt_configs.language_options.' + language),
            });
        });
        let oldTestPassRate = this.old(
            'test_pass_rate',
            this.wbtConfig.test_pass_rate
        );
        return {
            languages: languages,
            fields: {
                textVerification: verificationObject,
                languages: selectedLanguages,
                testPassRate:
                    typeof oldTestPassRate == 'number' ? oldTestPassRate : 50,
                skippable:
                    this.old('skippable', this.wbtConfig.skippable) || false,
                videoScrubbing:
                    this.old(
                        'video_scrubbing',
                        this.wbtConfig.video_scrubbing
                    ) || false,
                customLogo: this.getFilePath(
                    this.old('custom_logo', this.wbtConfig.custom_logo || '')
                ),
                customCss: this.getFilePath(
                    this.old('custom_css', this.wbtConfig.custom_css || '')
                ),
                customWelcomeImage: this.getFilePath(
                    this.old(
                        'custom_welcome_image',
                        this.wbtConfig.custom_welcome_image || ''
                    )
                ),
            },
            deletedLogo: false,
            deletedCss: false,
            deletedIntroImage: false,
            coursesSelected: this.preselectedCourses || [
                { label: this.trans('wbt_configs.all_courses'), value: 0 },
            ],
            languageOptions: languageOptionsArray,
            selectedText: {
                verification: {
                    value: 'de',
                    label: this.trans('wbt_configs.language_options.de'),
                },
            },
        };
    },
    computed: {
        coursesSelectedIds() {
            return JSON.stringify(this.coursesSelected.map(c => c.value));
        },
        languagesSelectedIds() {
            return JSON.stringify(this.fields.languages.map(c => c.value));
        },
    },
    methods: {
        onChangeLogo() {},
        onChangeCss() {},
        onChangeIntroImage() {},
        onRemoveLogo() {
            this.deletedLogo = true;
            this.fields.customLogo = '';
        },
        onRemoveCss() {
            this.deletedCss = true;
            this.fields.customCss = '';
        },
        onRemoveIntroImage() {
            this.deletedIntroImage = true;
            this.fields.customWelcomeImage = '';
        },
        courseSelectionChanged() {},

        getFilePath(input) {
            if (input !== '') {
                return '/storage/' + input;
            }

            return '';
        },
    },
};
</script>
